import './Popup.css';
import { ReactNode, useEffect, useRef } from 'react';

interface PopupProps {
  trigger: boolean;
  setTrigger: (value: boolean) => void;
  ariaLabel: string;
  children: ReactNode;
}

function Popup({
  trigger,
  setTrigger,
  ariaLabel,
  children,
}: PopupProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    if (!trigger) return;

    previouslyFocusedElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setTrigger(false);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements =
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        );

      if (!focusableElements?.length) return;

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (
        previouslyFocusedElementRef.current &&
        document.contains(previouslyFocusedElementRef.current)
      ) {
        previouslyFocusedElementRef.current.focus();
      }
    };
  }, [setTrigger, trigger]);

  return trigger ? (
    <div className="popup">
      <div
        className="popup-inner"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        <button
          type="button"
          ref={closeButtonRef}
          className="close-btn"
          onClick={() => setTrigger(false)}
        >
          Close
        </button>
        {children}
      </div>
    </div>
  ) : (
    <div></div>
  );
}

export default Popup;
